/** Shopify Predictive Search Implementation **/
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search-results');
    
    this.input.addEventListener('input', this.debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    
    this.input.addEventListener('focus', this.onFocus.bind(this));
    
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.close();
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFocus() {
    const searchTerm = this.input.value.trim();
    
    if (!searchTerm.length) return;
    
    this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,page,article`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.json();
      })
      .then((response) => {
        this.renderSearchResults(response);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.querySelector('#predictive-search-results-loading-state').setAttribute('aria-hidden', 'false');
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
  }

  setLiveRegionLoadedState() {
    this.querySelector('#predictive-search-results-loading-state').setAttribute('aria-hidden', 'true');
    this.predictiveSearchResults.setAttribute('aria-busy', 'false');
  }

  renderSearchResults(response) {
    this.predictiveSearchResults.innerHTML = this.getResultsMarkup(response);
    this.setLiveRegionLoadedState();
    this.open();
  }

  getResultsMarkup(response) {
    const products = response.resources.results.products || [];
    const collections = response.resources.results.collections || [];
    const pages = response.resources.results.pages || [];
    const articles = response.resources.results.articles || [];

    return `
      <div id="predictive-search-results">
        ${products.length ? this.getProductsMarkup(products) : ''}
        ${collections.length ? this.getCollectionsMarkup(collections) : ''}
        ${articles.length ? this.getArticlesMarkup(articles) : ''}
        ${pages.length ? this.getPagesMarkup(pages) : ''}
        ${this.getViewAllMarkup(response.terms)}
      </div>
    `;
  }

  getProductsMarkup(products) {
    return `
      <div class="predictive-search-results__products">
        <h3 class="predictive-search-results__heading">Products</h3>
        <ul class="predictive-search-results__list">
          ${products.map(product => `
            <li class="predictive-search-results__item">
              <a href="${product.url}" class="predictive-search-results__link">
                ${product.featured_image && product.featured_image.url ? `
                  <div class="predictive-search-results__image">
                    <img src="${product.featured_image.url}" alt="${product.featured_image.alt}" loading="lazy">
                  </div>
                ` : ''}
                <div class="predictive-search-results__item-content">
                  <h4 class="predictive-search-results__item-title">${product.title}</h4>
                  <p class="predictive-search-results__item-price">${this.formatMoney(product.price)}</p>
                </div>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  getCollectionsMarkup(collections) {
    return `
      <div class="predictive-search-results__collections">
        <h3 class="predictive-search-results__heading">Collections</h3>
        <ul class="predictive-search-results__list">
          ${collections.map(collection => `
            <li class="predictive-search-results__item">
              <a href="${collection.url}" class="predictive-search-results__link">
                <h4 class="predictive-search-results__item-title">${collection.title}</h4>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  getArticlesMarkup(articles) {
    return `
      <div class="predictive-search-results__articles">
        <h3 class="predictive-search-results__heading">Articles</h3>
        <ul class="predictive-search-results__list">
          ${articles.map(article => `
            <li class="predictive-search-results__item">
              <a href="${article.url}" class="predictive-search-results__link">
                <h4 class="predictive-search-results__item-title">${article.title}</h4>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  getPagesMarkup(pages) {
    return `
      <div class="predictive-search-results__pages">
        <h3 class="predictive-search-results__heading">Pages</h3>
        <ul class="predictive-search-results__list">
          ${pages.map(page => `
            <li class="predictive-search-results__item">
              <a href="${page.url}" class="predictive-search-results__link">
                <h4 class="predictive-search-results__item-title">${page.title}</h4>
              </a>
            </li>
          `).join('')}
        </ul>
      </div>
    `;
  }

  getViewAllMarkup(terms) {
    return `
      <div class="predictive-search-results__view-all">
        <a href="/search?q=${encodeURIComponent(terms)}" class="predictive-search-results__view-all-link">
          View all results for "${terms}"
        </a>
      </div>
    `;
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    
    const value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.'),
        dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        centsAmount = parts[1] ? (decimal + parts[1]) : '';

      return dollarsAmount + centsAmount;
    }

    let moneyFormat = theme.moneyFormat || "${{amount}}";
    
    switch (moneyFormat.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return moneyFormat.replace(placeholderRegex, value);
  }

  open() {
    this.predictiveSearchResults.classList.add('predictive-search-results--visible');
    this.predictiveSearchResults.removeAttribute('aria-hidden');
  }

  close() {
    this.predictiveSearchResults.classList.remove('predictive-search-results--visible');
    this.predictiveSearchResults.setAttribute('aria-hidden', true);
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);